import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2, Users, MessageSquareText, BarChart3, ChevronLeft, ChevronRight, Link2, Check, Download } from "lucide-react";
import { encuestasApi } from "../../lib/api";

function qrImgUrl(texto: string, size = 180) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(texto)}&format=png&margin=8`;
}

function descargarQR(texto: string, nombreArchivo: string) {
  const a = document.createElement("a");
  a.href = qrImgUrl(texto, 500) + "&download=1";
  a.download = `${nombreArchivo}.png`;
  a.click();
}

function EnlaceQrSection({ tokenQr, titulo }: { tokenQr: string; titulo: string }) {
  const [copiado, setCopiado] = useState(false);
  const url = `${window.location.origin}/encuesta-qr/${tokenQr}`;
  return (
    <div className="px-5 py-4 bg-indigo-50/40 border-b border-ink-200 flex items-center gap-4 shrink-0">
      <img src={qrImgUrl(url)} alt="QR de la encuesta" className="size-20 rounded-lg shrink-0 bg-white p-1 border border-ink-200" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-ink-600 uppercase tracking-wide mb-1">Enlace QR genérico</p>
        <p className="text-xs text-ink-500 font-mono break-all">{url}</p>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <button
          onClick={() => descargarQR(url, `encuesta-qr-${titulo.replace(/\s+/g, "-").toLowerCase()}`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-ink-100 text-ink-700 hover:bg-ink-200"
        >
          <Download className="size-3.5" /> Descargar
        </button>
        <button
          onClick={() => { navigator.clipboard.writeText(url); setCopiado(true); setTimeout(() => setCopiado(false), 2000); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700"
        >
          {copiado ? <Check className="size-3.5" /> : <Link2 className="size-3.5" />}
          {copiado ? "Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
}

// Paleta categórica validada (orden fijo, nunca ciclada) — hasta 5 opciones por pregunta.
const COLORES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4"];

function fmtFecha(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function EncuestaDetalleModal({ id, onClose }: { id: number; onClose: () => void }) {
  const [tab, setTab] = useState<"analitica" | "respondientes">("analitica");

  const { data, isLoading } = useQuery({
    queryKey: ["encuestas", id],
    queryFn: () => encuestasApi.detalle(id),
  });

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <div className="min-w-0">
            <h2 className="font-display font-bold text-ink-800 truncate">
              {isLoading ? "Cargando…" : data?.encuesta?.titulo}
            </h2>
            {data && (
              <p className="text-xs text-ink-500">
                {data.totales.enviadas} enviadas · {data.totales.respondidas} respondidas
                {data.totales.enviadas > 0 && (
                  <> · {Math.round((data.totales.respondidas / data.totales.enviadas) * 100)}% tasa de respuesta</>
                )}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700 shrink-0"><X className="size-5" /></button>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-ink-400"><Loader2 className="size-6 animate-spin mx-auto" /></div>
        ) : !data ? (
          <div className="p-12 text-center text-ink-400 text-sm">No se pudo cargar la encuesta</div>
        ) : (
          <>
            {data.encuesta.modo === "qr" && data.encuesta.tokenQr && (
              <EnlaceQrSection tokenQr={data.encuesta.tokenQr} titulo={data.encuesta.titulo} />
            )}
            <div className="flex border-b border-ink-200 shrink-0">
              <button onClick={() => setTab("analitica")}
                className={`flex-1 px-4 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
                  tab === "analitica" ? "border-brand-500 text-brand-700 bg-brand-50/40" : "border-transparent text-ink-500 hover:bg-ink-50"
                }`}>
                <BarChart3 className="size-4" /> Resultados
              </button>
              <button onClick={() => setTab("respondientes")}
                className={`flex-1 px-4 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
                  tab === "respondientes" ? "border-brand-500 text-brand-700 bg-brand-50/40" : "border-transparent text-ink-500 hover:bg-ink-50"
                }`}>
                <Users className="size-4" /> Respondientes ({data.respondientes.length})
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              {tab === "analitica" && data.preguntas.map((p: any) => (
                <div key={p.preguntaId}>
                  <h3 className="text-sm font-bold text-ink-800 mb-2">{p.texto}</h3>

                  {(p.tipo === "seleccion" || p.tipo === "rating") ? (
                    <BarraPregunta opciones={p.opciones} />
                  ) : (
                    <RespuestasTextoCarrusel respuestas={p.respuestasTexto ?? []} />
                  )}
                </div>
              ))}

              {tab === "respondientes" && (
                <div className="divide-y divide-ink-100 border border-ink-200 rounded-lg overflow-hidden">
                  {data.respondientes.length === 0 ? (
                    <p className="text-xs text-ink-400 italic p-4">Todavía no se envió a ningún cliente</p>
                  ) : (
                    data.respondientes.map((r: any) => (
                      <div key={r.envioId} className="px-3 py-2.5 flex items-center gap-3 text-sm">
                        <span className="flex-1 min-w-0 truncate text-ink-800 font-medium">{r.nombreCliente}</span>
                        <span className="text-xs text-ink-500 hidden sm:block">{r.telefono ?? "—"}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
                          r.estado === "respondida" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {r.estado === "respondida" ? "Respondida" : "Pendiente"}
                        </span>
                        <span className="text-[11px] text-ink-400 w-32 text-right shrink-0 whitespace-nowrap">
                          {fmtFecha(r.respondidoAt ?? r.enviadoAt)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Respuestas de texto libre: una grande a la vez, con flechas para moverse entre ellas. */
function RespuestasTextoCarrusel({ respuestas }: { respuestas: Array<{ id: number; texto: string; nombreCliente: string; createdAt: string }> }) {
  const [idx, setIdx] = useState(0);

  if (respuestas.length === 0) {
    return <p className="text-xs text-ink-400 italic">Sin respuestas de texto todavía</p>;
  }

  const i = Math.min(idx, respuestas.length - 1);
  const r = respuestas[i];

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-ink-200 p-5 min-h-[9rem]">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquareText className="size-4 text-ink-400 shrink-0" />
          <span className="text-sm font-medium text-ink-700">{r.nombreCliente}</span>
          <span className="text-xs text-ink-400 ml-auto">{fmtFecha(r.createdAt)}</span>
        </div>
        <p className="text-base text-ink-700 whitespace-pre-wrap">{r.texto}</p>
      </div>

      {respuestas.length > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setIdx((n) => Math.max(0, n - 1))} disabled={i === 0}
            className="p-1.5 rounded-full border border-ink-200 text-ink-500 hover:bg-ink-50 disabled:opacity-30 disabled:hover:bg-transparent">
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-xs text-ink-400 tabular-nums">{i + 1} / {respuestas.length}</span>
          <button onClick={() => setIdx((n) => Math.min(respuestas.length - 1, n + 1))} disabled={i === respuestas.length - 1}
            className="p-1.5 rounded-full border border-ink-200 text-ink-500 hover:bg-ink-50 disabled:opacity-30 disabled:hover:bg-transparent">
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function BarraPregunta({ opciones }: { opciones: Array<{ id: number; texto: string; cantidad: number }> }) {
  const total = opciones.reduce((s, o) => s + o.cantidad, 0);
  const max = Math.max(...opciones.map((o) => o.cantidad), 1);
  return (
    <div className="space-y-2">
      {opciones.map((o, i) => {
        const pct = total > 0 ? Math.round((o.cantidad / total) * 100) : 0;
        const anchoBarra = (o.cantidad / max) * 100;
        return (
          <div key={o.id} className="flex items-center gap-3 text-sm">
            <span className="w-32 shrink-0 truncate text-ink-700" title={o.texto}>{o.texto}</span>
            <div className="flex-1 h-6 rounded-md bg-ink-100 overflow-hidden">
              <div className="h-full rounded-md transition-all"
                style={{ width: `${anchoBarra}%`, background: COLORES[i % COLORES.length] }} />
            </div>
            <span className="w-16 shrink-0 text-right text-ink-600 tabular-nums">{o.cantidad} ({pct}%)</span>
          </div>
        );
      })}
      {total === 0 && <p className="text-xs text-ink-400 italic">Sin respuestas todavía</p>}
    </div>
  );
}
